import type { ComponentProps } from 'react';
import { Chip } from '@mui/material';

import { BadInputError } from '@watershed/errors/BadInputError';
import MoreIcon from '@watershed/icons/components/More';
import { DataGridCellExpandAction } from '@watershed/ui-core/components/DataGrid/DataGridCellAction';
import Pill from '@watershed/ui-core/components/Pill';
import PopoverButton from '@watershed/ui-core/components/PopoverButton';

import OverflowList from './OverflowList';

export type OverflowChipListItem<T> = T & {
  label: string;
};

export type OverflowChipListRenderItem<T> = (props: {
  item: OverflowChipListItem<T>;
  index: number;
  numShown: number;
  chipProps: ComponentProps<typeof Chip>;
  usePill?: boolean;
}) => React.ReactElement | null;

type PopoverProps<T> = {
  type: 'popover';
  renderPopover: RenderOverFlowMenuPopover<T>;
  onClickPopoverButton?: (e: React.MouseEvent) => void;
};

type DialogProps = {
  type: 'dialog';
  onClick: () => void;
};

type ItemDisplay<T> = PopoverProps<T> | DialogProps;

type RenderOverFlowMenuPopover<T> = (props: {
  items: Array<OverflowChipListItem<T>>;
  overflowItems: Array<OverflowChipListItem<T>>;
  nonOverflowItems: Array<OverflowChipListItem<T>>;
}) => JSX.Element;

interface OverflowListMenuProps<T> {
  items: Array<OverflowChipListItem<T>>;
  overflowItems: Array<OverflowChipListItem<T>>;
  itemDisplay: ItemDisplay<T>;
  testId?: string;
}

function OverflowListPopoverButton<T>({
  items,
  overflowItems,
  itemDisplay,
  testId,
}: Omit<OverflowListMenuProps<T>, 'itemDisplay'> & {
  itemDisplay: PopoverProps<T>;
}) {
  const { onClickPopoverButton, renderPopover } = itemDisplay;
  return (
    <PopoverButton
      flat
      endIcon={null}
      testId={testId}
      buttonLabel={
        overflowItems.length > 0 ? (
          `+${overflowItems.length}`
        ) : (
          <MoreIcon sx={{ marginTop: '6px' }} />
        )
      }
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onClickPopoverButton?.(e);
      }}
      sx={{
        color: (theme) => theme.typography.body2.color,
        fontSize: (theme) => theme.typography.body3.fontSize,
        fontVariantNumeric: 'tabular-nums',
        paddingInline: 0.75,
        paddingBlock: 0,
        height: (theme) => theme.spacing(3),
        minWidth: 'auto',
        flexShrink: 0,
        border: 0,
        boxShadow: 'none',
        bgcolor: 'transparent',
        // Because ObjectList disables direct PopoverButton child styles
        fontWeight: (theme) => `${theme.typography.fontWeightBold} !important`,
        '&:hover': {
          backgroundColor: (theme) => `${theme.palette.grey20} !important`,
        },
        '&:focus, &:focus-visible': {
          border: 0,
          boxShadow: 'none',
        },
      }}
      alignRight
    >
      {renderPopover({
        items,
        overflowItems,
        nonOverflowItems: items.filter((item) => !overflowItems.includes(item)),
      })}
    </PopoverButton>
  );
}

function OverflowListDialogButton<T>({
  overflowItems,
  itemDisplay,
}: Omit<OverflowListMenuProps<T>, 'itemDisplay'> & {
  itemDisplay: DialogProps;
}) {
  const { onClick } = itemDisplay;
  return (
    <>
      <span>
        {overflowItems.length > 0 ? `+${overflowItems.length}` : null}
      </span>
      <DataGridCellExpandAction onClick={onClick} />
    </>
  );
}

function OverflowListMenu<T>({
  items,
  overflowItems,
  itemDisplay,
  testId,
}: OverflowListMenuProps<T>) {
  if (itemDisplay.type === 'popover') {
    return (
      <OverflowListPopoverButton
        items={items}
        overflowItems={overflowItems}
        itemDisplay={itemDisplay}
        testId={testId}
      />
    );
  } else {
    return (
      <OverflowListDialogButton
        items={items}
        overflowItems={overflowItems}
        itemDisplay={itemDisplay}
      />
    );
  }
}

export interface OverflowChipListProps<T> {
  items: Array<OverflowChipListItem<T>>;
  itemDisplay: ItemDisplay<T>;
  alignItems?: React.ComponentProps<typeof OverflowList>['alignItems'];
  maxChipWidth?: string;
  maxVisibleItems?: number;
  alwaysRenderOverflow?: boolean;
  usePill?: boolean;
  testId?: string;
  onClickItem?: (item: OverflowChipListItem<T>, e: React.MouseEvent) => void;
  onDeleteItem?: (item: OverflowChipListItem<T>, e: React.MouseEvent) => void;
  renderItem?: OverflowChipListRenderItem<T>;
}

interface OverflowListItemProps<T> {
  item: OverflowChipListItem<T>;
  numShown: number;
  index: number;
  usePill?: boolean;
  maxChipWidth?: string;
  onClickItem?: (item: OverflowChipListItem<T>, e: React.MouseEvent) => void;
  onDeleteItem?: (item: OverflowChipListItem<T>, e: React.MouseEvent) => void;
  renderItem?: OverflowChipListRenderItem<T>;
}

function OverflowListItem<T>({
  item,
  numShown,
  index,
  usePill,
  maxChipWidth,
  onClickItem,
  onDeleteItem,
  renderItem,
}: OverflowListItemProps<T>) {
  const onClickChip = (): ((e: React.MouseEvent) => void) | undefined => {
    if (!onClickItem) return undefined;

    if (usePill) {
      throw new BadInputError('Pills do not support onClick');
    }

    return (e: React.MouseEvent) => {
      e.stopPropagation();
      onClickItem(item, e);
    };
  };

  const onDeleteChip = (): ((e: React.MouseEvent) => void) | undefined => {
    if (!onDeleteItem) return undefined;

    if (usePill) {
      throw new BadInputError('Pills do not support onDelete');
    }

    return (e: React.MouseEvent) => {
      e.stopPropagation();
      onDeleteItem(item, e);
    };
  };

  const props: ComponentProps<typeof Chip> = {
    label: item.label,
    sx: {
      maxWidth: maxChipWidth ?? undefined,
      minWidth: numShown === 1 ? 0 : undefined,
    },
    onClick: onClickChip(),
    onDelete: onDeleteChip(),
  };

  if (renderItem) {
    return renderItem({
      item,
      index,
      numShown,
      chipProps: props,
      usePill,
    });
  }

  return usePill ? (
    <Pill key={index} {...props} />
  ) : (
    <Chip key={index} {...props} />
  );
}

export default function OverflowChipList<T>({
  items,
  itemDisplay,
  alignItems,
  maxChipWidth,
  maxVisibleItems,
  alwaysRenderOverflow = true,
  usePill,
  testId,
  onClickItem,
  onDeleteItem,
  renderItem,
}: OverflowChipListProps<T>) {
  return (
    <OverflowList
      alwaysRenderOverflow={alwaysRenderOverflow}
      minVisibleItems={1}
      items={items}
      gap={0.5}
      alignItems={alignItems}
      maxVisibleItems={maxVisibleItems}
      itemRenderer={(item, numShown, index) => (
        <OverflowListItem
          key={`${index}-${item.label}`}
          item={item}
          numShown={numShown}
          index={index}
          usePill={usePill}
          maxChipWidth={maxChipWidth}
          onClickItem={onClickItem}
          onDeleteItem={onDeleteItem}
          renderItem={renderItem}
        />
      )}
      overflowRenderer={({ items, overflownItems }) => (
        <OverflowListMenu
          items={items}
          overflowItems={overflownItems}
          itemDisplay={itemDisplay}
          testId={testId}
        />
      )}
    />
  );
}
